import React, { type ChangeEvent } from 'react'

import { DraggableProvided } from 'react-beautiful-dnd'
import { HiOutlineMinusCircle } from 'react-icons/hi'
import { MdDragIndicator } from 'react-icons/md'

import { Box, Flex, IconButton, Select, Text } from '@chakra-ui/react'

import { AiOperatorActionType } from '@/graphql/generated/schemas'

import { AiOperatorModelT, Criterion, CriterionOption } from '../types/types'
import {
  AiOperatorCriterionMapper,
  DEFAULT_MODEL_ID,
  getModelsForCriterionDropdown,
} from '../utils/utils'
import { AiOperatorCriterionInput } from './AiOperatorCriterionInput'

const CRITERION_SELECT_WIDTHS = {
  withModelDropdown: {
    maxWidth: {
      base: '180px',
      sm: '220px',
      md: '350px',
      lg: '420px',
      xl: '500px',
      '2xl': '600px',
    },
    minWidth: {
      base: '120px',
      sm: '140px',
      md: '200px',
      lg: '250px',
      xl: '300px',
      '2xl': '350px',
    },
  },
  withoutModelDropdown: {
    maxWidth: {
      base: '160px',
      sm: '200px',
      md: '350px',
      lg: '400px',
      xl: '550px',
      '2xl': '700px',
    },
    minWidth: {
      base: '50px',
      sm: '60px',
      md: '100px',
      lg: '120px',
      xl: '150px',
      '2xl': '200px',
    },
  },
} as const

interface AiOperatorCriterionItemIProps {
  item: Criterion
  handleDelete: (id: string) => void
  handleUpdateCriterion: (id: string, newCriterion: CriterionOption) => void
  handleUpdateAction: (id: string, newAction: AiOperatorActionType) => void
  provided: DraggableProvided
  disabled: boolean
  availableCriteria: CriterionOption[]
  aiOperatorModels?: AiOperatorModelT[]
  isImplementer?: boolean
  onAiOperatorModelChange: (
    criterionId: string,
    modelId: string | undefined
  ) => void
}

export const AiOperatorCriterionItem = ({
  item,
  handleDelete,
  handleUpdateCriterion,
  handleUpdateAction,
  provided,
  disabled,
  availableCriteria,
  aiOperatorModels = [],
  isImplementer = false,
  onAiOperatorModelChange,
}: AiOperatorCriterionItemIProps) => {
  const actionOptions = [
    {
      value: AiOperatorActionType.ResolveAndMarkAsFalse,
      label:
        AiOperatorCriterionMapper[AiOperatorActionType.ResolveAndMarkAsFalse],
    },
    {
      value: AiOperatorActionType.FlagAndRelease,
      label: AiOperatorCriterionMapper[AiOperatorActionType.FlagAndRelease],
    },
  ]

  const handleCriterionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value
    const selectedCriterion = availableCriteria.find(
      (option) => option.id === selectedValue
    )

    if (selectedCriterion) {
      handleUpdateCriterion(item.id, selectedCriterion)
    }
  }

  const handleActionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedAction = event.target.value as AiOperatorActionType
    handleUpdateAction(item.id, selectedAction)
  }

  const selectedModelId = item.aiOperatorModelId || DEFAULT_MODEL_ID

  const modelsToShow = getModelsForCriterionDropdown(
    item,
    aiOperatorModels,
    disabled
  )

  const shouldShowModelDropdown = isImplementer && modelsToShow.length > 0

  const criterionWidths = shouldShowModelDropdown
    ? CRITERION_SELECT_WIDTHS.withModelDropdown
    : CRITERION_SELECT_WIDTHS.withoutModelDropdown

  return (
    <Flex
      ref={provided.innerRef}
      {...provided.draggableProps}
      alignItems='center'
      gap={3}
      maxWidth='1800px'
      mb={2}
      mx='auto'
      width='100%'
    >
      {/* Main Criteria Container */}
      <Flex
        alignItems='center'
        bg={disabled ? '#F7FAFC' : '#EEF4FC'}
        borderRadius='10px'
        flex='1'
        justifyContent='space-between'
        minHeight={{ base: '44px', md: '48px', '2xl': '56px' }}
        p={{ base: '10px 10px', md: '12px 12px', '2xl': '16px 20px' }}
      >
        <Flex alignItems='center' flexShrink={0} mr={2}>
          <Box
            {...provided.dragHandleProps}
            alignItems='center'
            display='flex'
            mr={2}
            opacity={disabled ? 0.5 : 1}
            pointerEvents={disabled ? 'none' : 'auto'}
          >
            <MdDragIndicator aria-label='Drag handle' />
          </Box>
          <Select
            _focus={{ border: 'none', boxShadow: 'none' }}
            _hover={{ border: 'none' }}
            aria-label='Action type'
            bg='white'
            border='none'
            borderRadius='lg'
            isDisabled={disabled}
            maxWidth={{
              base: '60px',
              sm: '80px',
              md: '130px',
              lg: '150px',
            }}
            minWidth={{
              base: '60px',
              sm: '80px',
              md: '130px',
              lg: '150px',
            }}
            onChange={handleActionChange}
            size='sm'
            value={item.action}
          >
            {actionOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {/* The criteria action, i.e flag or resolve */}
                {option.label}
              </option>
            ))}
          </Select>
        </Flex>

        <Flex
          alignItems='center'
          flex='1'
          gap={{ base: 1, md: 2, xl: 3, '2xl': 4 }}
          justifyContent='center'
          maxWidth='1200px'
          minWidth='0'
        >
          <Flex
            alignItems='center'
            flexShrink={0}
            justifyContent='center'
            minWidth={{ base: '20px', '2xl': '30px' }}
          >
            <Text fontSize={{ base: 'sm', '2xl': 'md' }} textAlign='center'>
              if
            </Text>
          </Flex>

          <Select
            _focus={{ border: 'none', boxShadow: 'none' }}
            _hover={{ border: 'none' }}
            aria-label='Criterion type'
            bg='white'
            border='none'
            borderRadius='lg'
            flex='1'
            isDisabled={disabled}
            isReadOnly
            {...criterionWidths}
            onChange={handleCriterionChange}
            size='sm'
            value={item.criterion.id}
          >
            {availableCriteria.map((option) => (
              <option key={option.id} value={option.id}>
                {/* The criteria description, i.e No Interaction with Door, Door Left Open, etc */}
                {option.description}
              </option>
            ))}
          </Select>

          <Flex
            alignItems='center'
            flexShrink={0}
            justifyContent='center'
            minWidth={{ base: '20px', '2xl': '30px' }}
          >
            <Text fontSize={{ base: 'sm', '2xl': 'md' }} textAlign='center'>
              is
            </Text>
          </Flex>
        </Flex>

        <Flex alignItems='center' flexShrink={0} ml={2}>
          <AiOperatorCriterionInput
            aria-label='Detection state'
            isDisabled={disabled}
            showModelDropdown={shouldShowModelDropdown}
            value={item.detected ? 'Detected' : 'Not Detected'}
          />
          {!disabled && (
            <IconButton
              _focus={{ border: 'none', boxShadow: 'none' }}
              aria-label='Delete criterion'
              colorScheme='gray'
              icon={<HiOutlineMinusCircle />}
              ml={{ base: 1, '2xl': 2 }}
              onClick={() => handleDelete(item.id)}
              size='sm'
              variant='ghost'
            />
          )}
        </Flex>
      </Flex>

      {/* AI Model Container */}
      {shouldShowModelDropdown && (
        <Flex
          alignItems='center'
          bg={disabled ? '#F7FAFC' : '#EEF4FC'}
          borderRadius='10px'
          flexShrink={0}
          minHeight={{ base: '44px', md: '48px', '2xl': '56px' }}
          minWidth='fit-content'
          p={{ base: '10px 10px', md: '12px 12px', '2xl': '16px 20px' }}
        >
          <Select
            _focus={{ border: 'none', boxShadow: 'none' }}
            _hover={{ border: 'none' }}
            aria-label='AI Operator Model'
            bg='white'
            border='none'
            borderRadius='lg'
            color='black'
            fontSize='sm'
            fontStyle='normal'
            fontWeight='medium'
            isDisabled={disabled}
            maxWidth={{
              base: '85px',
              sm: '100px',
              md: '120px',
              lg: '140px',
              xl: '160px',
              '2xl': '180px',
            }}
            minWidth={{
              base: '70px',
              sm: '85px',
              md: '100px',
              lg: '120px',
              xl: '140px',
              '2xl': '160px',
            }}
            onChange={(e) => onAiOperatorModelChange(item.id, e.target.value)}
            size='sm'
            sx={{
              '& option': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
            value={selectedModelId}
          >
            {modelsToShow.map((model) => (
              <option
                key={model.id}
                title={model.canonicalName}
                value={model.id}
              >
                {model.canonicalName}
              </option>
            ))}
          </Select>
        </Flex>
      )}
    </Flex>
  )
}
